import React, { useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useGamePlay } from "../../../providers/GamePlayContext";
import { useAuth } from "../../../providers/AuthContext";
import BgSpaceman from "../../../assets/images/bg-spaceman.jpg";
import { MdFullscreen } from "react-icons/md";
import { AppLoader } from "../../../components/ui/loaders/AppLoader";
import { SectionLoader } from "../../../components/ui/loaders/SectionLoader";
import debounce from "lodash.debounce";

const GamePlay = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams(); // Extracts query params
  const provider = searchParams.get("provider");
  const gameType = searchParams.get("type");
  const gameName = searchParams.get("name");

  const { user } = useAuth();

  const {
    gameUrl,
    setGameUrl,
    gameLoading,
    setGameLoading,
    handleGameLaunch,
    handleGameClose,
  } = useGamePlay();

  const [matchesMedia, setMatchesMedia] = useState({
    isMobile: window?.innerWidth <= 1024,
    isTablet: window?.innerWidth <= 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setMatchesMedia({
        isMobile: window?.innerWidth <= 1024,
        isTablet: window?.innerWidth <= 768,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scrolling when component mounts
  useEffect(() => {
    if (matchesMedia.isMobile || matchesMedia.isTablet) {
      const body = document.querySelector("body");
      if (gameUrl) {
        body.classList.add("lock-scroll");
        window.scrollTo(0, 0);
      } else {
        body.classList.remove("lock-scroll");
      }
      // Clean-up in case the component is unmounted
      return () => {
        body.classList.remove("lock-scroll");
      };
    }
  }, [gameUrl]);

  const iframeRef = useRef(null);

  const goFullScreen = () => {
    if (iframeRef.current) {
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      } else if (iframeRef.current.mozRequestFullScreen) {
        iframeRef.current.mozRequestFullScreen();
      } else if (iframeRef.current.webkitRequestFullscreen) {
        iframeRef.current.webkitRequestFullscreen();
      } else if (iframeRef.current.msRequestFullscreen) {
        iframeRef.current.msRequestFullscreen();
      }
    }
  };
  let isLocation = location.pathname.startsWith("/game-play");
  const closeGameHandler = () => {
    if (window.history.length > 1) {
      navigate("/casino");
      setGameUrl(null);
    }
    handleGameClose();
  };
  useEffect(() => {
    if (isLocation) {
      handleGameLaunch(id, provider);
    }
  }, []);

  return (
    <>
      <style jsx="true">
        {`
          .bg-fixed {
            background-size: cover;
            background-position: 50% 50%;
          }
        `}
      </style>
      {user ? (
        <div
          className="relative flex flex-col z-[1001] animate-fadeIn bg-fixed md:h-[calc(100vh-120px)] h-[calc(100vh-50px)]"
          style={{ backgroundImage: `url(${BgSpaceman})` }}
        >
          {gameUrl && gameLoading ? (
            <SectionLoader />
          ) : (
            <div className="py-5">
              {/* Content - Takes remaining height */}
              <div className="md:bg-black/50 bg-black md:p-5 md:relative fixed top-0 left-0 md:h-auto h-[100dvh] max-w-screen-lg w-full mx-auto">
                <div className="md:static fixed top-0 right-0 gap-1 flex justify-end items-center bg-body md:w-auto md:mb-3">
                  <button
                    title="Full Screen"
                    onClick={goFullScreen}
                    className="md:bg-[#181818] bg-body text-white md:w-10 md:h-10 w-6 h-6 md:rounded-md hidden md:flex items-center justify-center transition shadow-lg"
                    aria-label="Full Screen"
                  >
                    <MdFullscreen size={30} />
                  </button>
                  <button
                    title="Close Game"
                    onClick={closeGameHandler}
                    className="bg-[#181818] text-white w-10 h-8 rounded flex items-center justify-center hover:bg-red-600 transition shadow-lg"
                    aria-label="Close Game"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto overflow-x-hidden max-w-screen-lg mx-auto w-full">
                  <div
                    ref={iframeRef}
                    className="md:h-[calc(100vh-250px)] h-[100dvh]"
                  >
                    <iframe
                      src={gameUrl}
                      title="Game"
                      className="w-full h-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-center items-center min-h-[calc(-83px+100vh)]">
          <div className="aspect-square p-14 flex flex-col items-center justify-center bg-[#323232bf] border border-[#585858] rounded-md">
            <h1 className="text-white text-xl font-bold mb-5">
              {t("Log in & Play")}
            </h1>
            <button
              className="rounded-xl p-3 py-2 bg-primary text-white mt-4"
              onClick={() => window.location.reload()}
            >
              {t("Refresh")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

GamePlay.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GamePlay;
